// i18n Service - No AI, just static translations
type SupportedLanguage = 'en' | 'fr' | 'ar' | 'darija' | 'de' | 'es';

interface Translations {
  [key: string]: any;
}

let currentLanguage: SupportedLanguage = 'en';
let translations: Translations = {};

// Translation cache to avoid reloading the same language multiple times
const translationCache: Map<SupportedLanguage, Translations> = new Map();

// Track missing translation keys in development mode
const missingKeys: Set<string> = new Set();
// @ts-ignore - Vite provides import.meta.env
const isDevelopment = typeof import.meta !== 'undefined' && (import.meta.env?.DEV || import.meta.env?.MODE === 'development');

// Default English translations (fallback)
const defaultTranslations: Translations = {
  common: {
    welcome: "Welcome",
    dashboard: "Dashboard",
    logout: "Logout",
    login: "Login",
    register: "Register",
    email: "Email",
    password: "Password",
    name: "Name",
    whatsapp: "WhatsApp Number",
    save: "Save",
    cancel: "Cancel",
    close: "Close",
    loading: "Loading...",
    error: "Error",
    success: "Success",
    credits: "Credits",
    lowCredits: "Low Credits!",
    profile: "Profile",
    settings: "Settings",
    locked: "Locked",
    getPlanToUse: "Get a plan to use this tool",
    upgradeToAccess: "Upgrade to a paid plan to unlock all features and tools",
    freeToolsAvailable: "Free tools: Site Audit, Keyword Research, Competitor Analysis, AI Tools",
    previous: "Previous",
    next: "Next",
    openTool: "Open Tool",
    back: "Back"
  },
  auth: {
    loginTitle: "Login to Your Account",
    registerTitle: "Create New Account",
    forgotPassword: "Forgot Password?",
    alreadyHaveAccount: "Already have an account?",
    dontHaveAccount: "Don't have an account?",
    signIn: "Sign In",
    signUp: "Sign Up",
    loginError: "Login failed. Please check your credentials.",
    registerError: "Registration failed. Please try again.",
    whatsappRequired: "WhatsApp number is required"
  },
  dashboard: {
    welcomeBack: "Welcome back, {name}",
    selectTool: "Select a tool to start analyzing.",
    siteAudit: "Site Audit",
    siteAuditDesc: "Technical SEO check.",
    keywordGenius: "Keyword Genius",
    keywordGeniusDesc: "Find profitable terms.",
    competitorSpy: "Competitor Spy",
    competitorSpyDesc: "Content gap analysis.",
    roiSimulator: "ROI Simulator",
    rankTracking: "SERP Rank Tracking & Competitor Spy",
    rankTrackingDesc: "Track keyword rankings and spy on competitors",
    trackRanks: "Track Ranks",
    jsonldAnalyzer: "JSON-LD Analyzer"
  },
  tools: {
    siteAudit: "Expert Site Audit",
    keywordResearch: "Keyword Genius",
    competitorAnalysis: "Competitor Spy",
    roiCalculator: "ROI Simulator",
    analyze: "Analyze",
    analyzing: "Analyzing...",
    enterUrl: "Enter website URL",
    seedKeyword: "Seed Keyword",
    myUrl: "My URL",
    competitorUrl: "Competitor URL",
    targetMarket: "Target Market Configuration",
    country: "Country",
    language: "Language",
    search: "Search",
    compare: "Compare",
    resourcesSection: "SEO Resources & Learning",
    aiToolsSection: "AI-Powered Tools",
    learnSection: "Learn & Master SEO",
    reportsSection: "Reports & Analytics"
  },
  gsc: {
    title: "Google Search Console Analyzer",
    subtitle: "Analyze your GSC data to uncover SEO opportunities and track performance",
    dropFiles: "Drop files here or click to browse",
    clickToBrowse: "Click to browse",
    uploadInstructions: "Upload your Google Search Console CSV files",
    uploadDescription: "Upload the following CSV files exported from Google Search Console:",
    queriesFile: "Queries file (Search Performance > Queries)",
    pagesFile: "Pages file (Search Performance > Pages)",
    countriesFile: "Countries file (Search Performance > Countries)",
    devicesFile: "Devices file (Search Performance > Devices)",
    aiDetection: "AI will automatically detect opportunities and provide recommendations",
    analyzeButton: "Analyze GSC Data"
  },
  speedOptimizer: {
    title: "Speed Optimizer",
    cardDescription: "Optimize your website's loading speed and performance",
    subtitle: "Analyze and optimize your website's loading speed",
    urlPlaceholder: "Enter website URL (e.g., https://example.com)",
    analyzeButton: "Analyze Speed",
    deviceType: "Device Type",
    desktop: "Desktop",
    mobile: "Mobile"
  },
  competitor: {
    title: "Competitor Spy",
    myUrlPlaceholder: "My URL (https://...)",
    competitorUrlPlaceholder: "Competitor URL (https://...)",
    compareButton: "Compare Sites",
    analyzing: "Analyzing Fight...",
    contentGap: "Content Gap",
    strategy: "Strategy",
    strongestFeatures: "Competitor's Strongest Features",
    missingKeywords: "Missing Keywords",
    technicalAdvantages: "Technical Advantages",
    detailedReport: "Detailed Analysis Report",
    basicInfo: "Basic Information",
    metaDescription: "Meta Description",
    language: "Language",
    contentAnalysis: "Content Analysis",
    wordCount: "Word Count",
    imagesWithAlt: "Images with Alt Text",
    internalLinks: "Internal Links",
    topKeywords: "Top Keywords",
    technicalSEO: "Technical SEO",
    technologyStack: "Technology Stack"
  },
  roi: {
    title: "ROI Simulator",
    monthlyTraffic: "Monthly Traffic",
    conversionRate: "Conv. Rate (%)",
    valuePerLead: "Value / Lead (€)",
    estimatedRevenue: "Estimated Monthly Revenue",
    leadsPerMonth: "Leads / Month"
  },
  audit: {
    scanDirect: "Scan via URL",
    codeSourceTab: "Paste HTML Source",
    urlLabel: "Website URL",
    urlPlaceholder: "https://your-startup.com",
    htmlLabel: "HTML Source (paste here)",
    htmlPlaceholder: "<!-- Paste full HTML source here for a deep audit -->",
    htmlHelp: "Copy the full HTML source (Ctrl+U) for the most accurate audit.",
    loading: "Scanning site...",
    submitUrl: "Run Audit",
    submitHtml: "Analyze Source"
  },
  consulting: {
    title: "Book Consulting",
    subtitle: "Get expert SEO, WordPress, and WooCommerce advice",
    adminTitle: "Consulting Bookings Management",
    adminSubtitle: "Manage all consulting bookings and sessions",
    step1: "Service",
    step2: "Date & Time",
    step3: "Details",
    step4: "Review",
    step5: "Payment",
    step6: "Confirm",
    selectService: "Select Consulting Type",
    selectDuration: "Select Duration",
    popular: "POPULAR",
    next: "Next",
    back: "Back",
    selectDateTime: "Select Date & Time",
    selectDate: "Select Date",
    timezone: "Your Local Time",
    selectTime: "Select Time",
    loadingSlots: "Loading available slots...",
    noSlots: "No available slots for this date",
    yourDetails: "Your Details",
    fullName: "Full Name",
    email: "Email Address",
    telegram: "Telegram Username",
    optional: "optional",
    phone: "Phone Number",
    website: "Website URL",
    topic: "Consultation Topic",
    topicPlaceholder: "Describe what you need help with, your business goals, and what you've tried so far...",
    characters: "characters",
    legalConfirmation: "I confirm my business is legal and complies with accepted topics (IPTV services, Windows/Office/QuickBooks licensing, physical products, legal digital products, Shopify, WordPress/WooCommerce)",
    termsAccepted: "I agree to the terms and conditions including cancellation policy",
    review: "Review Your Booking",
    service: "Service",
    duration: "Duration",
    date: "Date",
    time: "Time",
    total: "Total",
    proceedToPayment: "Proceed to Payment",
    processing: "Processing...",
    payment: "Complete Payment",
    amount: "Amount",
    paymentNote: "Pay securely with PayPal or credit/debit card",
    payWithPayPal: "Pay with PayPal",
    paymentRedirect: "You will be redirected to PayPal to complete your payment. After payment, you will be redirected back here.",
    loadingPayPal: "Loading PayPal...",
    success: "Booking Confirmed!",
    successMessage: "Your booking has been confirmed. You will receive a confirmation email with Google Meet link shortly.",
    bookingReference: "Booking Reference",
    bookAnother: "Book Another Session",
    statsTotal: "Total Bookings",
    statsConfirmed: "Confirmed",
    statsCompleted: "Completed",
    statsCancelled: "Cancelled",
    statsRevenue: "Total Revenue",
    filterStartDate: "Start Date",
    filterEndDate: "End Date",
    filterStatus: "Status",
    filterType: "Type",
    all: "All",
    confirmed: "Confirmed",
    completed: "Completed",
    cancelled: "Cancelled",
    noShow: "No Show",
    search: "Search",
    searchPlaceholder: "Search bookings...",
    refresh: "Refresh",
    loading: "Loading bookings...",
    noBookings: "No bookings found",
    bookingId: "Booking ID",
    client: "Client",
    dateTime: "Date & Time",
    paymentStatus: "Payment",
    status: "Status",
    actions: "Actions",
    joinMeeting: "Join Meeting",
    cancel: "Cancel",
    view: "View",
    confirmCancel: "Are you sure you want to cancel this booking?"
  },
  admin: {
    title: "User Management",
    analytics: "Analytics",
    users: "Users",
    payments: "Payments",
    paypalSetup: "PayPal Setup",
    totalUsers: "Total Users",
    approvedUsers: "Approved Users",
    pendingUsers: "Pending Users"
  },
  paywall: {
    title: "Unlock Full Access",
    subtitle: "Your free trial has ended. Invest in your business to continue.",
    weekly: "Weekly",
    monthly: "Monthly",
    yearly: "Yearly",
    payWithPayPal: "Pay with PayPal",
    popular: "POPULAR"
  },
  verification: {
    title: "Email Verification Required",
    message: "Please verify your email address to receive your 10 free credits and access all features. Check your inbox for the verification code.",
    enterCode: "Enter Verification Code",
    verify: "Verify",
    resend: "Resend Code",
    codeSent: "Verification code sent to your email",
    verifyNow: "Verify Email Now"
  },
  language: {
    selectTitle: "Select Your Preferences",
    selectDescription: "Choose your language for analysis results and website interface, plus your target market",
    welcomeTitle: "Welcome! Choose Your Preferences",
    analysisLanguage: "Analysis Results Language",
    analysisLanguageDesc: "Language for audit results and reports",
    uiLanguage: "Website Interface Language",
    uiLanguageDesc: "Language for buttons, menus, and UI elements",
    aiAssistantLanguage: "AI Assistant Language",
    aiAssistantLanguageDesc: "Language for AI-generated explanations and recommendations",
    targetCountry: "Target Market (Country)",
    savePreferences: "Save Preferences",
    cancel: "Cancel"
  },
  auditProgress: {
    fetchingData: "Fetching & Extracting Data",
    enhancedAnalysis: "Enhanced Analysis",
    aiAnalysis: "AI Analysis",
    linkPerformance: "Link & Performance Analysis",
    overviewScore: "Overview & Score",
    startingExtraction: "Starting extraction...",
    waitingExtraction: "Waiting for extraction to complete...",
    waitingEnhanced: "Waiting for enhanced analysis...",
    waitingAI: "Waiting for AI analysis...",
    processingOverview: "Processing Overview & Score...",
    processingBrokenLinks: "Processing Broken Links...",
    processingRedirects: "Processing Redirects (301/302)...",
    processingMinification: "Processing Asset Minification (CSS/JS)...",
    analyzingWebsite: "Analyzing website...",
    analyzingBrokenLinks: "Analyzing broken links...",
    analyzingRedirects: "Analyzing redirects (301/302)...",
    analyzingMinification: "Analyzing asset minification...",
    extractedData: "Extracted data",
    streamingAnalysis: "Streaming analysis...",
    overallProgress: "Overall Progress",
    step: "Step",
    of: "of",
    processing: "Processing..."
  },
  seoGuide: {
    title: "SEO & AEO Master Guide",
    subtitle: "Learn the secrets of ranking on Google in 2025. Master AI detection, keyword research, and content optimization.",
    searchPlaceholder: "Search guides...",
    noResults: "No guides found. Try a different search term.",
    keyPoints: "Key Points",
    tips: "Pro Tips",
    examples: "Examples",
    stats: {
      guides: "Guides Available",
      categories: "Categories",
      free: "100%",
      access: "Free Access"
    },
    categories: {
      all: "All Guides",
      aiDetection: "AI Detection",
      seoBasics: "SEO Basics",
      keywordResearch: "Keyword Research",
      contentOptimization: "Content Optimization"
    },
    sections: {
      aiDetection: {
        title: "Avoid AI Detection Triggers",
        description: "Learn what makes content look AI-generated and how to write naturally.",
        items: {
          uniformStructure: "Uniform sentence structure and lack of variation",
          repetitivePhrasing: "Predictable and repetitive phrasing patterns",
          lowPerplexity: "Low perplexity (high predictability) of word sequences",
          lackOfAnecdotes: "Lack of personal anecdotes, insights, or lived experience",
          genericLanguage: "Overuse of generic language and clichés",
          roboticTone: "Overly formal or robotic language style",
          overOptimization: "Over-optimization for SEO that flags as unnatural writing",
          lackOfBurstiness: "Absence of complexity or variability in sentence length"
        },
        tips: {
          varySentences: "Vary sentence length - mix short punchy sentences with longer descriptive ones",
          addPersonalStories: "Add personal anecdotes, mistakes, or real experiences",
          useIdioms: "Use idioms, slang, or region-specific language naturally",
          allowImperfections: "Allow minor imperfections - a casual aside or sentence fragment"
        }
      },
      eeat: {
        title: "E-E-A-T Principles (2025)",
        description: "Experience, Expertise, Authoritativeness, and Trustworthiness - Google's ranking factors.",
        items: {
          experience: "Show real experience: Include personal or professional experience relevant to the topic",
          expertise: "Demonstrate expertise especially for YMYL topics (health, finance)",
          authoritativeness: "Build authority by linking to reputable sources and having backlinks from trusted sites",
          trustworthiness: "Ensure full trustworthiness: Accurate facts, updated info, transparent authorship"
        },
        examples: {
          experience: {
            label: "Experience Example",
            text: "Based on 10 years of clinical research, this article explains..."
          },
          expertise: {
            label: "Expertise Example",
            text: "Cite credentials or link to author bios explaining qualifications"
          }
        }
      },
      userIntent: {
        title: "Understand and Meet User Intent",
        description: "Write content that directly answers the user's question or need.",
        items: {
          answerDirectly: "Write content that directly answers the user's question or need",
          rightTone: "Use the right tone and depth based on intent (informational, navigational, transactional)",
          personalize: "Personalize content if relevant to location or demographics"
        },
        examples: {
          query: {
            label: "Intent Example",
            text: "If the query is 'best running shoes for flat feet,' focus on that, not just shoes in general"
          }
        }
      },
      keywordOptimization: {
        title: "Choose and Optimize Focus Keywords",
        description: "Select one primary keyword and use it strategically throughout your content.",
        items: {
          chooseFocus: "Select one primary keyword or keyphrase that clearly defines your article topic",
          placeInTitle: "Incorporate keyword naturally in: SEO title, meta description, first paragraph, and at least one H2/H3",
          useSynonyms: "Use synonyms and related terms to capture semantic variations",
          monitorPerformance: "Monitor keyword performance in Google Search Console and adjust accordingly"
        },
        tips: {
          specificKeywords: "Instead of 'cookies,' choose 'vegan chocolate chip cookies' for more specific targeting",
          naturalPlacement: "Place the focus keyword early in the text and meta elements naturally"
        }
      },
      contentStructure: {
        title: "Content Quality and Structure",
        description: "Organize your content for better readability and SEO performance.",
        items: {
          clearHeadings: "Use clear and organized headings (H1, H2, H3) for better structure",
          tableOfContents: "Use a Table of Contents leading to sections explaining each aspect",
          shortParagraphs: "Break content into short paragraphs for better readability",
          multimedia: "Use multimedia (images, videos) to support points and increase engagement"
        }
      },
      readability: {
        title: "Improve Readability for Better UX",
        description: "Make your content easy to read and understand for better user experience.",
        items: {
          shortSentences: "Keep 80% of sentences under 20 words",
          frequentSubheadings: "Use frequent subheadings (H2, H3) every 200–300 words to enhance scannability",
          transitionWords: "Include transition words ('however,' 'therefore,' etc.) in at least 30% of sentences",
          simpleLanguage: "Write in simple language aiming for 6th to 8th-grade reading level"
        },
        tips: {
          bulletPoints: "Use bullet points, numbered lists, and images to make content more engaging",
          varyLength: "Vary sentence length for rhythm and flow"
        }
      }
    }
  },
  aiTools: {
    title: "AI-Powered SEO Tools",
    subtitle: "Advanced AI tools to optimize your SEO and content",
    chat: {
      title: "AI SEO Chat",
      description: "Get personalized SEO advice",
      placeholder: "Ask about SEO...",
      startMessage: "Start a conversation about SEO..."
    },
    meta: {
      title: "Meta Tag Generator",
      description: "Generate optimized meta tags",
      generate: "Generate Meta Tags"
    },
    optimize: {
      title: "Content Optimizer",
      description: "Analyze and optimize content",
      analyze: "Analyze & Optimize (2 Credits)"
    },
    brief: {
      title: "Content Brief",
      description: "Generate content outlines",
      generate: "Generate Content Brief"
    },
    faq: {
      title: "FAQ Generator",
      description: "Create FAQ sections",
      generate: "Generate FAQ"
    },
    headlines: {
      title: "Headline Generator",
      description: "Generate A/B test headlines",
      generate: "Generate Headlines"
    }
  },
  strategy: {
    title: "SEO Strategy & Calendar",
    subtitle: "Generate a full SEO strategy and content calendar for your business.",
    topicLabel: "Your Niche / Main Topic",
    topicPlaceholder: "e.g., Local SEO for dentists in Casablanca",
    marketLabel: "Target Market",
    languageLabel: "AI Content Language",
    weeksLabel: "Strategy Duration (weeks)",
    planLabel: "Plan",
    freePlanInfo: "Free plan: you can generate up to 3 full articles using your remaining credits ({credits}).",
    paidPlanInfo: "Paid plan: you can generate up to 10 articles per week without using credits.",
    whatYouGet: "What you get",
    benefit1: "Full SEO content strategy overview",
    benefit2: "Weekly publishing calendar with ready topics",
    benefit3: "Article briefs ready to generate content",
    benefit4: "Articles generated in your chosen language",
    generateButton: "Generate Strategy & Calendar",
    generating: "Generating strategy...",
    overviewTitle: "Strategy Overview",
    metaInfoTitle: "Publishing Plan",
    planTypeLabel: "Plan type",
    durationLabel: "Duration",
    weeksShort: "weeks",
    frequencyLabel: "Posting frequency",
    maxPerWeekLabel: "Max articles / week",
    keywordsLabel: "Suggested Keywords",
    noKeywords: "No keywords detected.",
    calendarTitle: "Content Calendar",
    weekLabel: "Week {week}",
    keywordBadge: "Keyword",
    articlesTitle: "Generated Articles",
    articlesSubtitle: "Click on a post in the calendar to generate a full article. Content is ready to copy & paste.",
    articleReadyLabel: "Article Ready",
    languageBadge: "Language",
    noArticlesYet: "No articles generated yet. Use the calendar to generate your first article.",
    generateArticle: "Generate Article",
    generatingArticle: "Generating...",
    articleReadyButton: "Article Ready",
    copyArticle: "Copy Article",
    emptyTitle: "No strategy yet - let's create one",
    emptyDescription: "Describe your business, niche, or main topic above and click \"Generate Strategy & Calendar\".",
    errorTopicRequired: "Please enter a topic or niche before generating a strategy.",
    errorGeneric: "Failed to generate strategy. Please try again.",
    articleErrorGeneric: "Failed to generate article. Please check your credits and try again."
  },
  designmind: {
    title: "DesignMind AI - Website Mockup Generator",
    subtitle: "Generate intelligent website mockups with SEO & conversion strategy",
    premiumFeature: "Premium Feature",
    premiumDescription: "Upgrade to a paid plan to access DesignMind AI",
    credits: "Credits",
    cost: "Cost: 10 credits per generation",
    projectDefinition: "Project Definition",
    projectDescription: "Tell us about your business. Our AI will architect the perfect website structure.",
    language: "Language",
    industry: "Industry / Niche",
    businessModel: "Business Model",
    targetAudience: "Target Audience",
    goals: "Primary Goals",
    cms: "CMS Platform",
    competitors: "Competitors (Optional)",
    generate: "Generate Mockup",
    generating: "Generating Intelligent Mockup...",
    newProject: "New Project",
    exportJson: "Export JSON",
    overallStrategy: "Overall Strategy",
    generatedPages: "Generated Pages",
    pagesGenerated: "{count} pages generated",
    sections: "sections",
    seoOptimized: "SEO optimized",
    mockupData: "Mockup Data",
    viewFullJson: "View Full JSON",
    errorTitle: "Generation Failed",
    tryAgain: "Try Again",
    insufficientCredits: "Insufficient credits. DesignMind AI requires 10 credits per generation. You have {credits} credits.",
    requiresUpgrade: "DesignMind AI is only available for paid plans. Please upgrade to access this feature.",
    placeholderIndustry: "e.g. Sustainable Fashion, Dental Clinic",
    placeholderAudience: "e.g. CMOs at mid-sized tech companies",
    placeholderGoals: "e.g. Book a consultation, Free Trial",
    placeholderCompetitors: "e.g. competitor1.com"
  },
  reporting: {
    title: "SEO Reports & Analytics",
    subtitle: "View and download comprehensive SEO audit reports",
    infoTitle: "About Reports",
    infoText: "Generate comprehensive PDF reports containing all SEO analysis data including On-Page, Technical, Off-Page, and AEO scores, issues, and recommendations. Reports are generated from your website audit data.",
    auditHistory: "Audit History",
    noReports: "No audit reports yet",
    noReportsDesc: "Run a site audit to generate your first report",
    generatePDF: "Generate PDF",
    downloadPDF: "Download PDF Report",
    generating: "Generating...",
    howTo: "How to Generate Reports",
    step1: "Run a site audit using the Site Audit tool",
    step2: "View the detailed results on the audit results page",
    step3: "Click \"Download PDF Report\" to generate a comprehensive PDF",
    step4: "The PDF will include all SEO scores, issues, and recommendations",
    noReportData: "Full report data not available for this audit. Please run a new audit to generate a PDF report.",
    score: "Score",
    noReportDataBadge: "(No report data)"
  },
  seoResources: {
    title: "SEO Resources & Plugins",
    subtitle: "Essential WordPress plugins and Shopify apps for SEO, speed, security, and more",
    wordpress: "WordPress",
    shopify: "Shopify",
    categories: "Categories",
    speedOptimization: "Speed Optimization",
    seo: "SEO",
    imageOptimization: "Image Optimization",
    security: "Security",
    backup: "Backup",
    forms: "Forms",
    emailMarketing: "Email Marketing",
    reviews: "Reviews",
    conversionOptimization: "Conversion Optimization",
    essentialUtilities: "Essential Utilities",
    options: "Options",
    recommended: "Recommended",
    paid: "Paid",
    free: "Free",
    plugins: {
      WPRocket: {
        description: "Premium caching plugin for WordPress. Fast, reliable, and easy to use."
      },
      NitroPack: {
        description: "All-in-one speed optimization solution with automatic image optimization."
      },
      Perfmatters: {
        description: "Lightweight performance plugin to disable unnecessary features and scripts."
      },
      W3TotalCache: {
        description: "Free caching plugin with database, object, and page caching support."
      },
      WPFastestCache: {
        description: "Free caching plugin with simple setup and good performance."
      },
      Autoptimize: {
        description: "Free plugin to optimize CSS, JavaScript, and HTML automatically."
      },
      "WP-Optimize": {
        description: "Free plugin to clean database, compress images, and optimize performance."
      },
      LiteSpeedCache: {
        description: "Free caching plugin optimized for LiteSpeed servers."
      }
    },
    recommendedStack: "Recommended Stack",
    recommendedStackDesc: "Here's our recommended plugin stack for maximum performance:",
    stackWPRocket: "WP Rocket (Caching)",
    stackRankMath: "Rank Math (SEO)",
    stackSmush: "Smush (Image Optimization)",
    stackWordfence: "Wordfence (Security)",
    stackUpdraftPlus: "UpdraftPlus (Backup)",
    totalCost: "Total Cost",
    proTip: "Pro Tip",
    proTipDesc: "Start with the free plugins first, then upgrade to premium ones as your site grows."
  },
  results: {
    downloadPDF: "Download PDF Report",
    rescan: "Rescan",
    newScan: "New Scan",
    copy: "Copy",
    copied: "Copied!",
    print: "Print",
    exportJSON: "Export JSON",
    noIssues: "All Good!",
    noIssuesDesc: "No issues found. Great job!",
    pdfGenerationFailed: "Failed to generate PDF",
    unknownError: "Unknown error",
    overallPerformance: "Overall Performance",
    performanceTop: "Excellent",
    performanceMedium: "Good",
    performanceLow: "Needs Improvement",
    byOrca: "By Sofia Host LLC",
    auditReport: "Audit Report",
    pro: "Pro",
    summaryLabel: "Summary",
    categoryOnPage: "On-Page SEO",
    categoryTechnical: "Technical SEO",
    categoryOffPage: "Off-Page SEO",
    categoryAEO: "AEO (Answer Engine Opt.)",
    severityCritical: "Critical",
    severityHigh: "High",
    severityMedium: "Medium",
    severityLow: "Low",
    severityInfo: "Info",
    codeSnippet: "Code Snippet",
    expertAnalysis: "Expert Analysis",
    totalIssues: "Total Issues",
    priorityIssues: "Priority Issues",
    imagesWithAlt: "Images with Alt Text",
    adviceLabel: "Orca's Advice:",
    impactLabel: "Impact",
    solutionLabel: "Solution",
    actionPlan: "Action Plan",
    allIssues: "All ({count})",
    detailedActionPlan: "Detailed Action Plan",
    noIssuesTitle: "Perfect Score!",
    noIssuesMessage: "No issues found. Excellent work!",
    generatingPDF: "Generating...",
    enhancedAnalysis: "Enhanced SEO Analysis",
    enhancedMetaTags: "Meta Tags Analysis",
    enhancedContent: "Content Analysis",
    enhancedSSL: "SSL/HTTPS Security",
    technicalFiles: "Technical Files",
    mobileSEO: "Mobile SEO",
    websitePreview: "Website Preview",
    titleTag: "Title Tag",
    metaDescription: "Meta Description",
    length: "Length",
    characters: "characters",
    pixelWidth: "Pixel Width",
    willBeTruncated: "Will be truncated in Google",
    googlePreview: "Google Preview",
    desktop: "Desktop",
    mobile: "Mobile",
    headingsStructure: "Headings Structure",
    headings: "Headings",
    chars: "chars",
    contentAnalysis: "Content Analysis",
    altAttribute: "Alt Attribute",
    foundImages: "We found",
    imagesOnPage: "image(s) on this web page",
    noAltMissing: "No ALT attributes are missing",
    imagesMissingAlt: "image(s) missing ALT attributes",
    imagesMissingAltText: "Images Missing Alt Text",
    noAltText: "No alt text",
    lazyLoaded: "Lazy loaded",
    withAlt: "With Alt",
    lazyLoading: "Lazy Loading",
    responsive: "Responsive",
    inPageLinks: "In-Page Links",
    foundTotalLinks: "We found a total of",
    linksIncluding: "link(s) including",
    linksToFiles: "link(s) to files",
    internalLinks: "Internal Links",
    externalLinksFollow: "External Links: Follow",
    externalLinksNoFollow: "External Links: NoFollow",
    indexing: "Indexing",
    robotsTxt: "Robots.txt",
    found: "Found",
    notFound: "Not Found",
    sitemap: "Sitemap",
    canonical: "Canonical Tag",
    present: "Present",
    missing: "Missing",
    mobileFriendliness: "Mobile Friendliness",
    mobileFriendly: "Mobile-friendly viewport detected",
    notMobileFriendly: "Mobile viewport not properly configured",
    security: "Security",
    https: "HTTPS",
    enabled: "Enabled",
    disabled: "Disabled",
    dmarc: "DMARC Record",
    structuredData: "Structured Data",
    schemaTypesFound: "Schema types found",
    status: "Status",
    allMinified: "All Minified",
    needsOptimization: "Needs Optimization",
    total: "total",
    noBrokenLinks: "No broken links found!",
    noRedirects: "No redirects detected!",
    longTailKeywords: "Long-Tail Keywords (2-5 words)",
    top7Focused: "Top 7 Focused",
    singleKeywords: "Single Keywords",
    keywordExplanation: "This data represents the words and phrases that your page appears to be optimized around. We use natural language processing (NLP) to do this analysis. The numbers next to each word or phrase represents how often we detected them and their variants on the page.",
    imagesNotWebP: "Images Not WebP",
    format: "Format",
    position: "Position",
    quickWins: "Quick Wins",
    quickWinsDesc: "These issues can be fixed quickly and will have an immediate positive impact on your SEO score.",
    searchIssues: "Search issues...",
    all: "All",
    sortByPriority: "Sort by Priority",
    sortByImpact: "Sort by Impact",
    sortByEffort: "Sort by Effort",
    sortAlphabetical: "Sort Alphabetically",
    actionableOnly: "Show only actionable items",
    showing: "Showing",
    of: "of",
    estimatedTime: "Estimated Time",
    compactView: "Compact View",
    priorityMatrix: "Priority Matrix",
    critical: "Critical",
    high: "High",
    medium: "Medium",
    low: "Low",
    viewDetails: "View Details",
    impact: "Impact",
    howToFix: "How to Fix",
    codeExample: "Code Example",
    score: "Score",
    mobilePreview: "Mobile Preview",
    png: "PNG",
    jpeg: "JPG/JPEG",
    nonWebPFormat: "Non-WebP Format",
    language: "Language",
    declared: "Declared",
    detected: "Detected",
    linkDetails: "Link Details:",
    titleAnchor: "Title/Anchor",
    url: "URL",
    type: "Type",
    follow: "Follow",
    internal: "internal",
    external: "external",
    aiKeywordAnalysis: "AI Keyword Analysis",
    missingAlt: "Missing Alt",
    nonWebP: "Non-WebP",
    webp: "WebP",
    imageFormatAnalysis: "Image Format Analysis:",
    webpOptimal: "WebP (Optimal)",
    urlResolution: "URL Resolution:",
    urlResolutionGood: "Great, a redirect is in place to redirect traffic from your non-preferred domain.",
    allVersionsSameUrl: "All versions of your page point to the same URL.",
    resolvedUrl: "Resolved URL",
    redirectType: "Redirect Type",
    wwwNonWwwWarning: "Search engines see www and non-www as different websites. This means they could see duplicate content, which they don't like.",
    wwwNonWwwGood: "Fortunately your website redirects www and non-www to the same site.",
    headSectionAnalysis: "HEAD Section Analysis",
    issuesFound: "Issues found:",
    footerSectionAnalysis: "FOOTER Section Analysis",
    harmonyScore: "HEAD/FOOTER Harmony Score",
    overallHarmony: "Overall Harmony",
    performance: "Performance"
  },
  linkAnalysis: {
    title: "Link Analysis & Technical Checks",
    brokenLinks: "Broken Links (404 Errors)",
    brokenLinksFound: "{count} found",
    redirects: "Redirects Found",
    redirectsFound: "{count}",
    sitemapFound: "Sitemap Found",
    sitemapNotFound: "Sitemap Not Found",
    location: "Location:",
    foundIn: "Found in:",
    robotsTxt: "robots.txt file",
    commonLocation: "common location",
    sitemapAccessible: "Sitemap is accessible",
    noSitemap: "No XML sitemap was found. This can slow down search engine indexing.",
    recommendedLocations: "Recommended locations: /sitemap.xml, /sitemap_index.xml",
    linkStatistics: "Link Statistics",
    totalLinksChecked: "Total Links Checked",
    internalLinks: "Internal Links",
    externalLinks: "External Links",
    anchor: "Anchor:",
    status: "Status:",
    canonical: "Canonical",
    from: "From:",
    to: "To:"
  },
  performanceAudit: {
    title: "Performance Audit",
    overallScore: "Performance Score",
    minification: {
      title: "Asset Minification",
      subtitle: "Check if CSS and JavaScript files are minified",
      cssFiles: "CSS Files",
      jsFiles: "JavaScript Files",
      totalSavings: "Potential Savings",
      file: "File",
      originalSize: "Original Size",
      minifiedSize: "Minified Size",
      savings: "Savings",
      isMinified: "Already Minified",
      notMinified: "Not Minified",
      savingsPercentage: "Savings %"
    },
    cacheability: {
      title: "Browser Caching",
      subtitle: "Check if assets have proper cache headers",
      totalAssets: "Total Assets",
      cached: "Cached",
      uncached: "Not Cached",
      cacheControl: "Cache-Control",
      maxAge: "Max-Age",
      expires: "Expires",
      etag: "ETag",
      lastModified: "Last-Modified",
      recommendation: "Recommendation",
      notSet: "Not set",
      present: "Present"
    },
    images: {
      title: "Image Optimization",
      subtitle: "Analyze image formats, sizes, and optimization",
      totalImages: "Total Images",
      properlySized: "Properly Sized",
      hasLazyLoad: "Has Lazy Loading",
      hasDimensions: "Has Dimensions",
      needsOptimization: "Needs Optimization",
      notNextGen: "Not Next-Gen Format",
      format: "Format",
      dimensions: "Dimensions",
      currentSize: "Current Size",
      recommendations: "Recommendations",
      convertToWebP: "Convert to WebP or AVIF format",
      compressImage: "Compress image to reduce file size",
      addLazyLoad: "Add lazy loading attribute",
      addDimensions: "Add explicit width and height attributes"
    },
    recommendations: {
      title: "Performance Recommendations",
      high: "High Priority",
      medium: "Medium Priority",
      low: "Low Priority",
      category: "Category",
      issue: "Issue",
      action: "Recommended Action"
    }
  },
  landing: {
    noGetRichQuick: "No \"Get Rich Quick\" Schemes. Just Tools.",
    heroTitle1: "Stop Dreaming.",
    heroTitle2: "Start Analyzing.",
    heroSubtitle1: "Why SEO and AEO Matter More Than Paid Ads",
    heroSubtitle2: "Paid ads stop working the moment you stop paying. SEO and AEO build assets that work for you 24/7. Optimize your e-commerce store, dropshipping site, or digital product platform from homepage to product pages. Generate everything you need to rank #1 on Google, Bing, Yandex, and AEO. Stop renting traffic. Start owning it.",
    heroSubtitle3: "I'm",
    heroSubtitle4: "I built these tools because I was tired of fake gurus selling dreams. Online business is",
    heroSubtitle5: "math, data, and hard work",
    heroSubtitle6: "Use my personal tools to analyze the market, fix your SEO, and build a real business.",
    pricingTitle: "Simple, Transparent",
    pricingTitle2: "Pricing",
    pricingSubtitle: "Choose the plan that fits your needs. All plans include unlimited access to all tools.",
    perWeek: "per week",
    perMonth: "per month",
    perYear: "per year",
    save33: "Save 33%",
    featureUnlimited: "Unlimited SEO audits",
    featureKeywordsUnlimited: "Unlimited keyword research",
    featureCompetitorUnlimited: "Unlimited competitor analysis",
    featureAI: "All AI tools (Chat, Meta Tags, Content Optimizer, etc.)",
    featureStrategyUnlimited: "SEO Strategy & Content Calendar",
    featureDesignMindUnlimited: "DesignMind AI - Website Mockup Generator",
    featureBacklinksUnlimited: "Full Backlink Builder (27+ platforms)",
    feature301: "301 Redirect Guide & Checklist",
    featurePDFUnlimited: "PDF Reports & Analytics",
    featureSupport: "Priority support",
    featureBestValue: "Best value - Save 33%",
    featuresTitle: "Complete SEO & AEO Suite",
    featuresSubtitle: "Everything you need to dominate search engines, optimize content, and build a sustainable online business. No guessing. Just data.",
    featureSiteAudit: "Expert Site Audit",
    featureSiteAuditDesc: "Comprehensive technical SEO analysis using GPT AI. Check on-page, off-page, technical SEO, and AEO scores. Find broken links, redirect issues, sitemap problems, and get actionable recommendations.",
    featureKeywords: "Keyword Genius",
    featureKeywordsDesc: "Discover profitable keywords with search volume, competition analysis, and difficulty scores. Target specific markets and languages. Find long-tail opportunities your competitors miss.",
    featureCompetitor: "Competitor Spy",
    featureCompetitorDesc: "Analyze competitor strategies, content gaps, backlink profiles, and SEO tactics. See what they're doing right and identify opportunities to outrank them.",
    featureAIChat: "AI SEO Chat Assistant",
    featureAIChatDesc: "Get personalized SEO advice 24/7. Ask questions about your website, get optimization tips, and receive expert guidance tailored to your business. The AI analyzes your site before responding.",
    featureMetaTags: "Auto Meta Tag Generator",
    featureMetaTagsDesc: "Generate optimized SEO titles and meta descriptions automatically. Perfect length, keyword-optimized, and compelling. Save hours of manual work.",
    featureContentOptimizer: "Content Optimizer & Scorer",
    featureContentOptimizerDesc: "Analyze and optimize your content for SEO. Get a score, identify issues, and receive specific recommendations to improve rankings and readability.",
    featureContentBrief: "Content Brief Generator",
    featureContentBriefDesc: "Create comprehensive content outlines with target keywords, structure, headings, and optimization guidelines. Perfect for writers and content teams.",
    featureFAQ: "FAQ Generator",
    featureFAQDesc: "Generate SEO-optimized FAQ sections for your pages. Answer common questions, target featured snippets, and improve user experience.",
    featureHeadlines: "Title & Headline Generator",
    featureHeadlinesDesc: "Create multiple headline variations for A/B testing. Optimize for clicks, SEO, and conversions. Test what works best.",
    featureStrategy: "SEO Strategy & Content Calendar",
    featureStrategyDesc: "Get a complete SEO strategy with weekly content calendar. Generate ready-to-publish articles (3 free, 10/week for paid plans). All optimized and ready to copy-paste.",
    featureDesignMind: "DesignMind AI - Website Mockup Generator",
    featureDesignMindDesc: "Generate intelligent website mockups with SEO and conversion strategy. Get page structures, layouts, and design recommendations tailored to your business.",
    featureResources: "SEO Resources & Plugins",
    featureResourcesDesc: "Curated lists of essential WordPress plugins and Shopify apps. Speed, SEO, security, backup, and more. Recommendations and pro tips included.",
    featureBacklinks: "Backlink Builder & 301 Redirect Guide",
    featureBacklinksDesc: "Free backlink sources from 27+ platforms. Complete 301 redirect guide for expired domains. Todo tracking system. Premium feature for monthly plans.",
    featureROI: "ROI Simulator",
    featureROIDesc: "Calculate potential revenue from SEO improvements. Real business math. See ROI projections based on traffic growth and conversion rates.",
    featurePDF: "PDF Reports & Analytics",
    featurePDFDesc: "Download comprehensive PDF reports with all SEO data. Share with clients, track progress, and keep records of all your audits.",
    startUsingTools: "Start Using All Tools Now",
    truthTitle: "The Truth They Don't Want You to Know",
    moroccanLieTitle: "The Digital Marketing Lie",
    moroccanLieSubtitle: "Why Influencers Are Setting You Up to Fail",
    problemTitle: "The Problem in Today's Online Business Scene",
    gurusTellYou: "What online \"gurus\" keep telling you:",
    guru1: "\"Run Google Ads and you'll be rich!\"",
    guru2: "\"Facebook Ads are the secret to success!\"",
    guru3: "\"Just spend 500-1000 € on ads daily!\"",
    guru4: "\"Scale with Meta Ads!\"",
    brutalTruth: "The brutal truth they won't tell you:",
    brutalTruthText: "These influencers make money from: Affiliate commissions from Facebook/Google ad courses, Agency fees managing your ad budget, Course sales teaching you the \"ad secrets\", and Your repeated failures that keep you coming back for more courses.",
    adsFirstTitle: "Why The \"Ads-First\" Strategy Is Destroying Businesses",
    adsFirstCol1: "Ads-First Approach (What They Sell)",
    adsFirstCol2: "SEO/AEO Reality (What Works)",
    adsFirst1: "Spend 10,000 €/month minimum",
    adsFirst2: "Start with 0 € investment",
    adsFirst3: "Traffic stops when money runs out",
    adsFirst4: "Traffic grows while you sleep",
    adsFirst5: "Compete against big brands",
    adsFirst6: "Dominate specific niches",
    adsFirst7: "Need constant budget increases",
    adsFirst8: "Exponential returns over time",
    adsFirst9: "You're dependent on platforms",
    adsFirst10: "You own your audience",
    adsFirst11: "Zero after 6 months if you stop",
    adsFirst12: "Asset worth millions after 2 years",
    realNumbersTitle: "The Real Numbers They Hide From You",
    adsApproachTitle: "Ads Approach (What influencers push):",
    adsApproach1: "Month 1-3: Spend 15,000 €, make 20,000 € → 5,000 € profit",
    adsApproach2: "Month 4-6: Spend 20,000 €, make 28,000 € → 8,000 € profit",
    adsApproach3: "Stop paying = 0 traffic",
    adsApproachTotal: "Total invested: 105,000 € over 6 months",
    adsApproachOwn: "You own: NOTHING",
    seoApproachTitle: "SEO/AEO Approach (What actually builds wealth):",
    seoApproach1: "Month 1-3: Create 20 optimized articles → 0 €, small traffic",
    seoApproach2: "Month 4-6: Traffic growing, 50 articles total → 5,000 € profit",
    seoApproach3: "Month 7-12: 100 articles, 500 visitors/day → 30,000 €/month",
    seoApproach4: "Year 2: 1,500 visitors/day → 100,000+ €/month",
    seoApproachTotal: "Total invested: Your time + maybe 3,000 € for hosting/tools",
    seoApproachOwn: "You own: A DIGITAL ASSET worth millions",
    seoVsAdsTitle: "SEO/AEO vs. Paid Ads for Business Growth",
    factor: "Factor",
    costOverTime: "Cost over time",
    costOverTimeSEO: "Decreases (one-time effort, long-term gains)",
    costOverTimeAds: "Increases continuously (pay per click/impression)",
    sustainability: "Sustainability",
    sustainabilitySEO: "Builds long-term asset",
    sustainabilityAds: "Stops when budget runs out",
    trust: "Trust & credibility",
    trustSEO: "High (organic = trustworthy)",
    trustAds: "Lower (people skip ads)",
    ctr: "Click-through rate",
    ctrSEO: "Higher for organic results",
    ctrAds: "Lower (ad blindness)",
    roiTimeline: "ROI timeline",
    roiTimelineSEO: "Slower start, exponential growth",
    roiTimelineAds: "Immediate but linear",
    compounding: "Compounding effect",
    compoundingSEO: "Yes (content builds on itself)",
    compoundingAds: "No (each campaign isolated)",
    whySeoWinsTitle: "Why SEO and AEO Win for Long-Term Business Building:",
    whySeoWins1: "SEO (Search Engine Optimization) and AEO (Answer Engine Optimization) create owned assets that work for you 24/7 without ongoing costs. When you rank organically, you're building equity—each piece of content can attract customers for months or years. People trust organic results more than ads, leading to better conversion rates.",
    whySeoWins2: "Paid ads are like renting attention; SEO/AEO is like owning property. While ads deliver immediate traffic, they require constant spending. The moment you stop paying, traffic disappears. Meanwhile, a well-optimized article you wrote two years ago can still generate leads today at zero additional cost.",
    whySeoWins3: "For sustainable business growth, SEO/AEO provides compounding returns. Your tenth article benefits from the authority of your first nine. With ads, your hundredth campaign costs the same as your first. Smart businesses use ads for quick wins while building their SEO/AEO foundation for lasting success.",
    aboutTitle: "About Sofia Host LLC",
    aboutText1: "We are Sofia Host LLC, a team of self-made entrepreneurs who've tried many strategies in online business. We're not millionaires selling you fake dreams like most online 'gurus' - we're a team who's learned what actually works through real experience.",
    aboutText2: "The truth they won't tell you: online business is just like any other business. You need time, you need hard work, and you need to build real assets. There's no magic key - the 'secret' is giving yourself time to learn and execute properly.",
    aboutText3: "I build tools and share knowledge to help people understand that SEO and AEO are how you build sustainable businesses, not burning money on ads that stop working the moment you stop paying.",
    aboutText4: "I work with people who value truth over hype, who want to build something real, not chase fantasies.",
    aboutText5: "Real value attracts real people. If you're ready to learn what actually works - the strategies, the systems, the honest path to building online - I'm here to help. But if you're looking for overnight millions and empty promises, we're not your people.",
    followInstagram: "Follow on Instagram",
    founderBuilder: "Founder & Builder",
    founderQuote: "\"The tool doesn't make you rich. YOU make you rich. The tool just makes you faster.\"",
    whyChooseTitle: "Why Choose OrcaTools?",
    whyChooseSubtitle: "Stop wasting money on ads. Build a real business with SEO and AEO.",
    buildAssetsTitle: "Build Real Assets",
    buildAssetsDesc: "Optimize your e-commerce store, dropshipping site, or digital product platform from homepage to product pages. Generate everything you need to rank #1 on Google, Bing, Yandex, and AEO. Build equity, not dependency.",
    noGuessingTitle: "No More Guessing",
    noGuessingDesc: "Data-driven decisions. Know your keywords, competitors, and opportunities. No more blind spending.",
    sustainableGrowthTitle: "Sustainable Growth",
    sustainableGrowthDesc: "Compound returns. Each piece of content makes the next one more powerful. Exponential growth over time.",
    startBuildingCTA: "Start Building Your Digital Asset Today",
    freeAnalyses: "10 free analyses to get started. No credit card required.",
    footerText: "Built for Real Entrepreneurs.",
    followUs: "Follow us:",
    memberLogin: "Member Login",
    dashboardPreviewTitle: "See OrcaTools in Action",
    dashboardPreviewSubtitle: "Real screenshots from the dashboard. See how powerful our tools are.",
    consultingBadge: "Professional Consulting",
    consultingTitle: "Need Expert Help?",
    consultingSubtitle: "Book a one-on-one consulting session with Sofia Host LLC. Get personalized SEO, WordPress, and WooCommerce advice tailored to your business.",
    consultingSEO: "SEO Consulting",
    consultingSEODesc: "Get expert advice on technical SEO, on-page optimization, keyword strategy, and content planning.",
    consultingWordPress: "WordPress Optimization",
    consultingWordPressDesc: "Optimize your WordPress site for speed, SEO, security, and conversions. Plugin recommendations and setup guidance.",
    consultingWooCommerce: "WooCommerce Consulting",
    consultingWooCommerceDesc: "E-commerce optimization, product page SEO, conversion rate optimization, and store performance tuning.",
    consultingDuration: "30-90 minutes",
    consultingCTA: "Ready to Get Expert Help?",
    consultingCTADesc: "Book a session directly from your dashboard. Choose your preferred date and time, and get personalized advice to grow your business.",
    consultingBookNow: "Book a Consulting Session",
    featureRankTracking: "SERP Rank Tracking & Competitor Spy",
    serpToolDescription: "Track keyword rankings, analyze SERP features, and spy on competitors. See where you rank and identify opportunities to outrank your competition.",
    featureJsonLd: "JSON-LD Structured Data Analyzer",
    featureJsonLdDesc: "Deep analysis of JSON-LD structured data implementation. Score your schema markup across 4 categories (Basic, Organization, SEO, Advanced) and get actionable recommendations for better SEO and AEO performance.",
    consultingPrice: "€29-€57",
    seoAeo: "SEO/AEO",
    paidAds: "Paid Ads"
  },
  backlinks: {
    title: "Backlink Builder & 301 Redirect Guide",
    subtitle: "Free backlink sources and 301 redirect strategies to boost your SEO",
    premiumRequired: "Monthly Plan Required",
    upgradeTitle: "Upgrade Required",
    upgradeMessage: "This feature requires a monthly plan or higher. Upgrade to access the full backlink builder, 301 redirect guide, and todo tracking system.",
    viewPlans: "View Plans & Upgrade",
    freeBacklinks: "Free Backlink Sources",
    redirectGuide: "301 Redirect Guide",
    todoList: "Action Checklist",
    resetTodos: "Reset all todos",
    reset: "Reset",
    noTodos: "No tasks yet. Click on items below to add them to your checklist.",
    completed: "completed",
    resetConfirm: "Are you sure you want to reset all todos?",
    addToTodo: "Add to checklist",
    visitSite: "Visit site",
    linksAllowed: "Links allowed:",
    approval: "Approval:",
    traffic: "Traffic potential:",
    business: "Business",
    tech: "Tech",
    creative: "Creative",
    issuuDesc: "Publish PDFs and documents. Great for portfolios, reports, and ebooks.",
    bestFor: "Best for:",
    avoidPenalization: "How to Avoid Google Penalization",
    bestPractices: "✅ Best Practices for Quality Backlinks",
    topTier: "⭐ TOP TIER - BEST FOR ARTICLE PUBLISHING",
    web2: "📝 WEB 2.0 BLOGGING PLATFORMS (FULL CONTROL)",
    community: "🎓 COMMUNITY & NICHE PLATFORMS",
    qa: "🗣️ Q&A PLATFORMS (ARTICLE-LENGTH ANSWERS)",
    document: "📚 DOCUMENT PUBLISHING",
    techCategory: "💻 TECH/DEVELOPER FOCUSED",
    creativeCategory: "🎨 CREATIVE PLATFORMS",
    recommendedStarter: "🎯 Recommended Starter Pack (Maximum Impact)",
    starterDesc: "Start with these 5 - they're the easiest and highest ROI:",
    mediumStarter: "Publish 2 articles/week, cross-post from your blog",
    linkedinStarter: "Publish 1 professional article/week",
    quoraStarter: "Answer 5 questions/week in your niche with detailed responses",
    devtoStarter: "Publish tutorials/guides",
    blogStarter: "Build your own blog subdomain",
    whatIs301: "🔍 What is the 301 Redirect Method?",
    redirectExplanation: "Simple explanation: You buy an expired domain that already has good backlinks and high DA/DR, then you \"redirect\" it to your website so all that SEO power (link juice) transfers to your site.",
    howItWorks: "How it works:",
    step1: "Domain XYZ.com expires (previous owner didn't renew)",
    step2: "XYZ.com has 500 backlinks, DR 40, DA 35",
    step3: "You buy XYZ.com for $50-500",
    step4: "You set up a 301 redirect from XYZ.com → YourWebsite.com",
    step5: "All the backlinks and authority from XYZ.com now boost YOUR website",
    googleUpdate: "⚠️ CRITICAL: Google's 2024 Update on Expired Domains",
    whatsBanned: "What's BANNED:",
    banned1: "Buying 50+ expired domains and redirecting them all",
    banned2: "Buying domains completely unrelated to your niche",
    banned3: "Automated expired domain networks",
    whatsOk: "What's STILL OK:",
    ok1: "Buying 1-3 relevant expired domains",
    ok2: "Redirecting domains in the SAME niche as yours",
    ok3: "\"Merger technique\" (acquiring a similar business)",
    bottomLine: "Bottom line: As long as the domain is RELEVANT to your niche and you're not doing it at massive scale, you're fine.",
    checklist: "🎯 Complete Checklist Before Buying",
    checkNiche: "Check niche relevance (MUST PASS)",
    checkDA: "Domain Authority: DR/DA 20+ (PASS)",
    checkSpam: "Spam Score: Under 30% (MUST PASS)",
    checkWayback: "Wayback Machine: Legit content, no spam (MUST PASS)",
    checkIndex: "Google Index: Still indexed (PASS)",
    checkBacklinks: "Backlink Quality: Real sites, not spam (PASS)",
    checkDMCA: "DMCA Check: No copyright issues (MUST PASS)",
    checkBlacklist: "Blacklist Check: Not blacklisted (MUST PASS)",
    checkAge: "Domain Age: 5+ years old (NICE TO HAVE)",
    howToRedirect: "🛠️ How to Do 301 Redirect - Step by Step",
    optionA: "OPTION A: Redirect in Hostinger",
    hostinger1: "Buy the expired domain at Namecheap, GoDaddy, etc.",
    hostinger2: "Point domain to Hostinger nameservers",
    hostinger3: "Add domain to Hostinger hPanel",
    hostinger4: "Set up 301 redirect using Hostinger's Redirect Tool or .htaccess file",
    optionB: "OPTION B: Redirect in Namecheap",
    namecheap1: "Buy/transfer expired domain to Namecheap",
    namecheap2: "Go to Domain List → Manage → Redirect Domain",
    namecheap3: "Choose \"Permanent Redirect (301)\"",
    namecheap4: "Enter your target URL and save",
    codeExamples: "💡 Code Examples",
    basicRedirect: "Basic Domain Redirect (.htaccess)",
    pageRedirect: "Page-to-Page Redirects (Best Method)",
    timeline: "⏱️ Timeline & Expectations",
    timeline1: "Google discovers the redirect",
    timeline2: "Backlinks start transferring authority",
    timeline3: "Full SEO impact visible",
    timeline4: "Rankings stabilize",
    commonMistakes: "🚨 Common Mistakes to Avoid",
    mistake1: "Buying irrelevant domains (biggest mistake)",
    mistake2: "Redirecting spammy domains",
    mistake3: "Buying too many domains at once (looks suspicious)",
    mistake4: "Redirecting to homepage only (redirect to relevant pages)",
    mistake5: "Not checking Wayback Machine",
    mistake6: "Ignoring spam score",
    // Platform descriptions
    mediumDesc: "Publish unlimited articles, add multiple links per article",
    linkedinDesc: "Publish long-form articles on LinkedIn Pulse",
    devtoDesc: "Tech/developer blogging platform",
    hashnodeDesc: "Developer blogging with custom domain option",
    hackernoonDesc: "Tech journalism platform",
    wordpressDesc: "Full blog at yourname.wordpress.com",
    bloggerDesc: "Google-owned blog platform",
    tumblrDesc: "Microblogging + long articles",
    wixDesc: "Free website + blog",
    weeblyDesc: "Free website with blog",
    substackDesc: "Newsletter platform (publish on web too)",
    hubpagesDesc: "Article publishing with revenue sharing",
    ezineDesc: "Article directory",
    articlesbaseDesc: "Article submission platform",
    quoraDesc: "Answer questions with detailed responses",
    redditDesc: "Post in relevant subreddits",
    slideshareDesc: "Upload presentations with links",
    issuDesc: "Publish PDFs as digital publications",
    scribdDesc: "Document publishing platform",
    googledocsDesc: "Share public documents",
    githubDesc: "README files in repositories",
    gitlabDesc: "Similar to GitHub",
    hacksterDesc: "Hardware/IoT project platform",
    behanceDesc: "Portfolio with project descriptions",
    dribbbleDesc: "Design showcase",
    unlimited: "Yes, unlimited in articles",
    multiple: "Yes, multiple links per article",
    contextual: "Yes, contextual links",
    yesUnlimited: "YES, unlimited",
    yesInBlog: "YES in blog posts",
    yes: "YES",
    twoLinks: "YES, 2 links per article (to your site)",
    yesInBio: "YES, in author bio",
    yesInArticle: "YES, in article + author box",
    yesWhenRelevant: "YES, when relevant to answer",
    yesReadRules: "YES, but read subreddit rules first",
    yesInPresentation: "YES, in presentation + description",
    yesInDocument: "YES, in document",
    yesInDocuments: "YES, in documents",
    yesUnlimitedMarkdown: "YES, unlimited in markdown",
    yesInProject: "YES, in project descriptions",
    yesInProfile: "YES, in profile + shot descriptions",
    instant: "Instant publishing",
    editorial24: "Editorial review (usually 24-48 hours)",
    instantGoogle: "Instant (Google account needed)",
    quality24: "Quality review (24 hours)",
    editorialSlower: "Editorial review (slower)",
    quickReview: "Quick review",
    instantMayMod: "Instant (may get moderated if spammy)",
    dependsSubreddit: "Depends on subreddit (some auto-approve)",
    high: "HIGH - millions of readers",
    mediumHigh: "MEDIUM-HIGH for tech content",
    medium: "MEDIUM for tech",
    highTech: "HIGH for tech/startup content",
    lowGrows: "LOW initially, grows over time",
    lowInitially: "LOW initially",
    mediumHigh2: "MEDIUM-HIGH",
    lowMedium: "LOW-MEDIUM",
    low: "LOW",
    veryHigh: "VERY HIGH",
    veryHighViral: "VERY HIGH if post goes viral",
    mediumTech: "MEDIUM for tech audience",
    mediumHardware: "MEDIUM for hardware projects",
    mediumDesign: "MEDIUM for design",
    lowIndexed: "LOW directly, but indexed by Google",
    anyNiche: "Any niche - business, tech, lifestyle, etc.",
    businessNiche: "Business, marketing, professional content",
    techNiche: "Programming, web development, tech tutorials",
    developers: "Developers, tech enthusiasts",
    techStartup: "Tech, crypto, startups, AI",
    anyNiche2: "Any niche, long-term blogging",
    googleServices: "Any niche, especially if you use other Google services",
    creativeNiche: "Creative content, visual + text mix",
    portfolio: "Portfolio + blog combo",
    simpleBlog: "Simple blog + business site",
    newsletters: "Newsletters, thought leadership",
    howTo: "How-to guides, tutorials, reviews",
    howToEducational: "How-to articles, educational content",
    businessMarketing: "Business, marketing articles",
    anyNicheQuestions: "Any niche, answering specific questions",
    nicheCommunities: "Niche communities, discussions",
    businessEducational: "Business, educational content as slides",
    magazines: "Magazines, ebooks, reports",
    longForm: "Long-form documents, guides",
    detailedGuides: "Detailed guides, resources",
    openSource: "Open source projects, code documentation",
    codeProjects: "Code projects, tech docs",
    arduino: "Arduino, Raspberry Pi, maker projects",
    designers: "Designers, photographers, creatives",
    uiux: "UI/UX designers, illustrators",
    practice1: "Write 800-2000 words minimum",
    practice1Desc: "Longer = better rankings",
    practice2: "Add 1-3 backlinks per article",
    practice2Desc: "Don't spam links",
    practice3: "Use natural anchor text",
    practice3Desc: "\"check out this guide\" not \"best SEO tools\"",
    practice4: "Write original content",
    practice4Desc: "Don't copy-paste across platforms",
    practice5: "Engage with communities",
    practice5Desc: "Comment on others' posts",
    practice6: "Be patient",
    practice6Desc: "Backlinks take 2-4 weeks to show effect",
    avoid1: "Don't spam - You'll get banned",
    avoid2: "Don't copy-paste - Each platform should have unique content",
    avoid3: "Don't over-optimize - No keyword stuffing",
    avoid4: "Don't only promote - Provide value first"
  },
  serp: {
    title: "SERP Rank Tracker",
    subtitle: "See which websites rank in the top positions for any keyword",
    keyword: "Keyword",
    keywordPlaceholder: "Enter keyword to track (e.g., \"best SEO tools\")",
    engine: "Search Engine",
    location: "Location",
    language: "Language",
    device: "Device",
    date: "Date (Optional)",
    resultCount: "Results to Show",
    search: "Search Rankings",
    searching: "Searching...",
    results: "Search Results",
    showing: "Showing",
    of: "of",
    noResults: "Enter a keyword and click \"Search Rankings\" to see which websites rank in the top positions.",
    remainingSearches: "Remaining searches",
    costPerSearch: "Cost",
    showingTop: "Showing top"
  },
  jsonld: {
    title: "JSON-LD Analyzer",
    subtitle: "Analyze and optimize structured data on your website",
    urlPlaceholder: "Enter website URL (e.g., https://example.com)",
    analyze: "Analyze JSON-LD",
    analyzing: "Analyzing...",
    note: "Note",
    noteText: "This tool will fetch your website, detect all JSON-LD structured data, analyze it for SEO best practices, and provide recommendations.",
    excellent: "Excellent",
    good: "Good",
    fair: "Fair",
    poor: "Poor",
    outOf: "out of",
    categoryBreakdown: "Category Breakdown",
    category: {
      basic: "Basic",
      organization: "Organization",
      seo: "SEO",
      advanced: "Advanced"
    },
    tab: {
      summary: "Summary",
      checks: "Checks",
      recommendations: "Recommendations"
    },
    rawJsonLd: "Raw JSON-LD",
    schemaTypes: "Schema Types",
    properties: "Properties",
    more: "More",
    issues: "Issues",
    checks: "Checks",
    points: "points"
  },
  profile: {
    title: "Profile",
    editProfile: "Edit Profile",
    gettingStartedTitle: "Getting Started Guide",
    step1Title: "Step 1: Verify Your Email",
    emailVerified: "✅ Your email is verified. You have access to all features!",
    emailNotVerified: "⚠️ Please verify your email address to receive your 10 free credits and unlock all features. Check your inbox for the verification code.",
    step2Title: "Step 2: Understand Credits",
    creditsIntro: "Most tools cost",
    oneCredit: "1 credit",
    creditsPerUse: "per use. Some tools have different costs:",
    creditCost1: "SEO Audit, Keyword Research, Competitor Analysis:",
    creditCost2: "Content Optimizer:",
    creditCost3: "Speed Optimizer:",
    twoCredits: "2 credits",
    fiveCredits: "5 credits",
    creditsRemaining: "You have {credits} credits remaining. Upgrade to a paid plan for unlimited credits!",
    unlimitedCredits: "✅ You have unlimited credits with your paid plan!",
    step3Title: "Step 3: Start Using Tools",
    toolsIntro: "Navigate to the dashboard and choose a tool:",
    tool1Name: "SEO Audit:",
    tool1Desc: "Analyze any website for SEO issues",
    tool2Name: "Keyword Research:",
    tool2Desc: "Find profitable keywords for your niche",
    tool3Name: "Competitor Analysis:",
    tool3Desc: "See what your competitors are doing",
    tool4Name: "AI Tools:",
    tool4Desc: "Generate content, meta tags, FAQs, and more",
    tool5Name: "SERP Rank Tracker:",
    tool5Desc: "Track your keyword rankings",
    step4Title: "Step 4: Upgrade When Ready",
    upgradeMessage: "When you run out of credits, upgrade to Weekly ($10), Monthly ($29), or Yearly ($290) for unlimited access to all tools!",
    paidPlanMessage: "You're on the {plan} plan. Enjoy unlimited access to all features!"
  }
};

// Load translation file using dynamic import (non-blocking)
const loadTranslations = async (lang: SupportedLanguage): Promise<Translations> => {
  // Check cache first
  if (translationCache.has(lang)) {
    return translationCache.get(lang)!;
  }

  // If already English, use default and cache it
  if (lang === 'en') {
    translationCache.set('en', defaultTranslations);
    return defaultTranslations;
  }

  try {
    // Try dynamic import - Vite will handle this
    const module = await import(`../src/locales/${lang}.json`);
    const loadedTranslations = module.default || module;
    if (loadedTranslations && typeof loadedTranslations === 'object') {
      // Cache the loaded translations
      translationCache.set(lang, loadedTranslations as Translations);
      return loadedTranslations as Translations;
    }
  } catch (error) {
    console.warn(`[i18n] Failed to load ${lang} translations, using English:`, error);
  }
  
  // Fallback to English and cache it
  translationCache.set(lang, defaultTranslations);
  return defaultTranslations;
};

// Initialize i18n (async but non-blocking)
export const initI18n = async (lang: SupportedLanguage = 'en'): Promise<void> => {
  currentLanguage = lang;
  translations = await loadTranslations(lang);
};

// Get translation
export const t = (key: string, params?: Record<string, string>): string => {
  // If translations not loaded yet, use default
  if (Object.keys(translations).length === 0) {
    translations = defaultTranslations;
  }

  const keys = key.split('.');
  
  // Helper function to get value from a translations object
  const getValue = (trans: Translations): string | null => {
    let value: any = trans;
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return null;
      }
    }
    return typeof value === 'string' ? value : null;
  };
  
  // Try current translations first
  let value = getValue(translations);
  
  // If not found, try defaultTranslations as fallback
  if (!value) {
    value = getValue(defaultTranslations);
  }
  
  // If still not found, log warning in development mode
  if (!value) {
    if (isDevelopment && !missingKeys.has(key)) {
      missingKeys.add(key);
      console.warn(`[i18n] Missing translation key: "${key}" (Language: ${currentLanguage})`);
      
      // Log all missing keys periodically (every 10 new keys)
      if (missingKeys.size % 10 === 0) {
        console.warn(`[i18n] Total missing translation keys: ${missingKeys.size}`);
        console.warn(`[i18n] Missing keys:`, Array.from(missingKeys).slice(-10));
      }
    }
    return key;
  }
  
  // Replace parameters
  if (params) {
    return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
      return params[paramKey] || match;
    });
  }
  
  return value;
};

// Get all missing translation keys (for debugging)
export const getMissingKeys = (): string[] => {
  return Array.from(missingKeys);
};

// Clear missing keys cache (useful for testing)
export const clearMissingKeys = (): void => {
  missingKeys.clear();
};

// Get current language
export const getCurrentLanguage = (): SupportedLanguage => {
  return currentLanguage;
};

// Set language
export const setLanguage = async (lang: SupportedLanguage): Promise<void> => {
  // Only reload if language actually changed
  if (lang !== currentLanguage || Object.keys(translations).length === 0) {
    await initI18n(lang);
  }
  
  currentLanguage = lang;
  localStorage.setItem('orca_ui_language_code', lang);
  
  // Dispatch language change event for real-time updates
  window.dispatchEvent(new CustomEvent('languageChange', { 
    detail: lang 
  }));
  
  // Also trigger a storage event for cross-tab synchronization
  window.dispatchEvent(new StorageEvent('storage', {
    key: 'orca_ui_language_code',
    newValue: lang,
    oldValue: currentLanguage,
    storageArea: localStorage
  }));
};

// Preload a language (useful for performance optimization)
export const preloadLanguage = async (lang: SupportedLanguage): Promise<void> => {
  if (!translationCache.has(lang)) {
    await loadTranslations(lang);
  }
};

// Preload all languages (useful for offline support)
export const preloadAllLanguages = async (): Promise<void> => {
  const languages: SupportedLanguage[] = ['en', 'fr', 'ar', 'darija', 'de', 'es'];
  await Promise.all(languages.map(lang => preloadLanguage(lang)));
};

// Map UI language names to codes
export const getLanguageCode = (languageName: string): SupportedLanguage => {
  const map: Record<string, SupportedLanguage> = {
    'English': 'en',
    'French': 'fr',
    'Arabic': 'ar',
    'Darija/French': 'darija',
    'Darija': 'darija',
    'German': 'de',
    'Spanish': 'es',
    'Deutsch': 'de',
    'Español': 'es',
    'Français': 'fr',
    'العربية': 'ar'
  };
  return map[languageName] || 'en';
};

// Get language name from code
export const getLanguageName = (code: SupportedLanguage): string => {
  const map: Record<SupportedLanguage, string> = {
    'en': 'English',
    'fr': 'Français',
    'ar': 'العربية',
    'darija': 'Darija',
    'de': 'Deutsch',
    'es': 'Español'
  };
  return map[code] || 'English';
};

// Check if a language is RTL (Right-to-Left)
export const isRTL = (lang?: SupportedLanguage): boolean => {
  const language = lang || currentLanguage;
  // Arabic and Darija (Arabic script) are RTL
  // All other languages (en, fr, de, es) are LTR
  return language === 'ar' || language === 'darija';
};

// Get text direction for a language
export const getTextDirection = (lang?: SupportedLanguage): 'rtl' | 'ltr' => {
  const language = lang || currentLanguage;
  // Arabic and Darija (Arabic script) are RTL
  // All other languages are LTR
  return (language === 'ar' || language === 'darija') ? 'rtl' : 'ltr';
};

// Initialize on load with default language (non-blocking, doesn't wait)
// Use setTimeout to avoid blocking module load
setTimeout(() => {
  initI18n('en').catch(err => {
    console.warn('Failed to initialize i18n:', err);
    // Set default translations as fallback
    translations = defaultTranslations;
  });
}, 0);
